import { useLayoutEffect, useRef, useState } from 'react';
import { AudioLines, Mic, Plus, Square } from 'lucide-react';
import { composerDir } from '../../../lib/composer-direction';
import { isImeComposing } from '../../../lib/ime-composition';
import type { Dictation } from '../../../lib/use-dictation';
import { composerMicMode } from '../../../lib/voice-note-recorder';
import type { VoiceNoteComposer } from '../../../lib/use-voice-note';
import { Button, IconButton } from '../ui';
import { ICON_BTN } from './styles';
import { CameraMenuIcon, ContactMenuIcon, FileMenuIcon, LocationMenuIcon, PhoneMenuIcon, SendIcon, TimeMenuIcon, TranscribingSpinner } from './icons';
import { AttachMenu, type AttachMenuItem } from './AttachMenu';
import { RecordingBar } from './RecordingBar';
import { VoiceNoteNotice } from './VoiceNoteNotice';
import styles from './ComposerPill.module.css';

// The one Symphony composer — the Figma "Prompt input": a 100px white box laid out
// as a column (text field on top, control row pinned to the bottom). Used by BOTH
// the global bottom composer (HomeComposer) and the in-chat composer (ChatSurface)
// so they are pixel-identical. Presentation only — the parent owns the value, the
// file input, dictation, and what "send" does.
export interface ComposerPillProps {
  /**
   * Rest as ONE LINE and open to the full pill on focus — the docked home bar (Figma's reference build
   * draws a 43px input + Send there, where this pill is a 118px column).
   *
   * Opt-in and default OFF, so the chat's composer is untouched: it is always expanded, because there
   * the composer IS the screen's purpose. Collapsed hides the control row rather than shrinking it —
   * a half-height row of round buttons reads as broken, and the row is one click away.
   */
  collapsible?: boolean;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  onAttach: () => void;
  attachLabel: string;
  // When provided, the "+" opens the attach PANEL (Take Photo or Video / Choose file / Contact / Location /
  // Share a time) instead of firing the file picker directly. Left unset (e.g. HomeComposer) → the
  // "+" stays a bare file-picker button. Everything below file+contact is an optional extra layered
  // on top, so a surface only offers what it can actually perform.
  onAttachContact?: () => void;
  // The panel's heading — native's `chat.attach` row. Required alongside `onAttachContact`.
  attachTitle?: string;
  chooseFileLabel?: string;
  contactLabel?: string;
  // Take Photo or Video — the file picker handed straight to the camera. Only wired where the browser
  // can honour it (see lib/camera-capture.ts); unset everywhere else, so the row simply isn't there.
  onTakePhoto?: () => void;
  takePhotoLabel?: string;
  // Photo from your phone — the desktop's QR hand-off of this chat to the phone's camera. Offered where
  // Take Photo is not (no camera within reach) and only once there is a conversation to hand off.
  onPhonePhoto?: () => void;
  phonePhotoLabel?: string;
  onAttachLocation?: () => void;
  locationLabel?: string;
  onAttachTime?: () => void;
  shareTimeLabel?: string;
  dictation: Dictation;
  dictateLabel: string;
  transcribingLabel?: string;
  cancelLabel: string;
  // Tap-to-record VOICE NOTE, the mic's other half. The button is MODAL, exactly as it is on both
  // phones: with an empty composer a tap records audio that is SENT as a message; with text in the
  // field the same tap dictates into it (`composerMicMode`, sampled at tap time). Unset → the mic
  // only ever dictates, which is what the composer did before — so a surface that has nowhere to
  // send a recording simply doesn't pass it.
  voiceNote?: VoiceNoteComposer;
  // Screen-reader/tooltip text for the mic in voice-note mode ("Record a voice note").
  recordVoiceNoteLabel?: string;
  // The recorder bar's two controls.
  discardRecordingLabel?: string;
  sendVoiceNoteLabel?: string;
  // Start a live voice call. Distinct from dictation (speech → text in the
  // box) and from a voice note (recorded audio sent as a message): this opens
  // a real-time conversation. Unset → no call affordance. The call surface itself
  // owns mute/settings/end (VoiceCallOverlay) — the composer only launches it.
  onStartCall?: () => void;
  // Screen-reader/tooltip text for the call launcher (e.g. "Start a call").
  startCallLabel?: string;
  // The launcher's VISIBLE label — the design's "Speak" pill (Figma 12835:78977).
  speakLabel?: string;
  // Keep the "Speak" pill showing whenever the field is EMPTY, even while it's focused — for the
  // auto-focused mobile FAB (HomeComposer), which otherwise opens focused and only ever shows the
  // disabled Send arrow. The in-chat composer leaves this off, so focusing its empty field still
  // yields to Send (its resting-vs-typing swap).
  speakWhenEmpty?: boolean;
  canSend: boolean;
  sendLabel: string;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onInputActiveChange?: (active: boolean) => void;
  showStop?: boolean;
  onStop?: () => void;
  stopLabel?: string;
  autoFocus?: boolean;
  // Optional reply-preview node rendered inside the pill, above the textarea.
  replyPreview?: React.ReactNode;
  // Scale the dictation waveform up for the full-size chat (Figma "Voice Recording" L) — the compact
  // panel keeps the small design bars. Off by default.
  recordingLarge?: boolean;
}

export function ComposerPill({
  collapsible = false,
  value,
  onChange,
  onSubmit,
  placeholder,
  onAttach,
  attachLabel,
  onAttachContact,
  attachTitle,
  chooseFileLabel,
  contactLabel,
  onTakePhoto,
  takePhotoLabel,
  onPhonePhoto,
  phonePhotoLabel,
  onAttachLocation,
  locationLabel,
  onAttachTime,
  shareTimeLabel,
  dictation,
  dictateLabel,
  transcribingLabel,
  cancelLabel,
  voiceNote,
  recordVoiceNoteLabel,
  discardRecordingLabel,
  sendVoiceNoteLabel,
  onStartCall,
  startCallLabel,
  speakLabel,
  speakWhenEmpty,
  canSend,
  sendLabel,
  onPaste,
  onInputActiveChange,
  showStop,
  onStop,
  stopLabel,
  autoFocus,
  replyPreview,
  recordingLarge,
}: ComposerPillProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Focus + caret-to-end on mount. A layout effect keeps it in the same frame as
  // the mount so mobile Safari opens the keyboard.
  useLayoutEffect(() => {
    if (!autoFocus) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [autoFocus]);
  // Focus drives the Speak↔Send swap (Figma "Keyboard Open" vs "Speak Default"): the Speak pill is
  // the RESTING affordance of an empty composer, but the moment the field is focused it yields to the
  // (disabled) Send arrow, exactly as typing does.
  const [focused, setFocused] = useState(false);
  const dictating = dictation.status === 'recording';
  const recordingNote = !!voiceNote?.active;
  const recording = dictating || recordingNote;
  const transcribing = dictation.status === 'transcribing';
  const voiceNoteError = voiceNote?.error ?? null;
  const effectivePlaceholder = transcribing ? transcribingLabel ?? placeholder : placeholder;
  // The single modal mic. An ACTIVE dictation session is latched — a tap stops it whatever the
  // text now says — otherwise the mode is sampled from the draft at TAP TIME. Shared 1:1 with the
  // phones' `micTapped`.
  const micMode = voiceNote?.supported ? composerMicMode(value) : 'dictation';
  const micLabel = micMode === 'voiceNote' ? recordVoiceNoteLabel ?? dictateLabel : dictateLabel;
  const onMicTap = () => {
    if (dictation.status !== 'idle') return;
    if (micMode === 'voiceNote') voiceNote!.start();
    else dictation.start();
  };
  // The mic is offered when EITHER capability is there. Gating it on dictation alone hid it
  // outright in a browser with no Web Speech API (Firefox) — which can still record a voice note.
  const micAvailable = dictation.supported || !!voiceNote?.supported;

  // Recording keeps the horizontal bar (waveform + timer) — and so does the voice-note notice,
  // which stands in the same slot; otherwise the column layout — field on top, control row
  // pinned to the bottom.
  // Does focus sit anywhere inside the pill — the field OR one of its controls? `onBlur`'s
  // `relatedTarget` is where focus is GOING, so a move between two children of the pill never reads
  // as leaving it. (It is null when focus leaves the document entirely, which correctly collapses.)
  const [focusWithin, setFocusWithin] = useState(false);

  // Collapsed only while it is genuinely idle: focus, a draft in progress, or either recording state
  // all mean the composer is in use and must be its full self.
  //
  // ⚠️ It reads focus on the WHOLE PILL, not on the textarea. Clicking attach or mic blurs the field,
  // so keying this on the textarea alone hid the control row in the same tick — the control was gone
  // before its own click could land, and neither an attachment-only send nor dictation could be
  // started from an empty composer at all.
  const collapsed = collapsible && !focusWithin && !value.trim() && !recording && !voiceNoteError;

  // ONE Send control, rendered by both layouts — the expanded control row and the collapsed single
  // line. Defining it twice is how the two would drift apart on the next change to either.
  const sendControl = (
    <IconButton
      variant="contained"
      color="standard"
      size="small"
      type="submit"
      disabled={!canSend}
      aria-label={sendLabel}
      className={`${styles.swapIn} ${styles.chatRound} ${styles.sendButton}`}
    >
      <SendIcon />
    </IconButton>
  );

  const containerStyle: React.CSSProperties = recording || voiceNoteError
    ? {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderRadius: 20,
        background: 'var(--ds-surface-sunken)',
        padding: 12,
        boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)',
        transition: 'box-shadow 160ms ease',
      }
    : {
        display: 'flex',
        // Collapsed is one text line (the field's own 40) inside a tighter 8px padding, so the docked
        // bar reads as a single line rather than a block. Expanded is the Figma prompt box.
        minHeight: collapsed ? 40 : 118,
        // Figma composer spec (node 14701:126613, "Prompt box" — 720×118): 12px padding all sides, 20 radius.
        padding: collapsed ? 'var(--ds-space-8)' : 12,
        // Collapsed is the reference bar's own shape — field and Send side by side on one line —
        // where expanded is the Figma prompt box, field over its control row.
        flexDirection: collapsed ? 'row' : 'column',
        gap: collapsed ? 'var(--ds-space-8)' : 0,
        justifyContent: 'space-between',
        alignItems: collapsed ? 'center' : 'flex-start',
        alignSelf: 'stretch',
        borderRadius: 20,
        background: 'var(--ds-surface-sunken)',
        // Flat resting elevation — no accent focus ring (Symphony composer spec).
        boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)',
        // The attach panel spans this card and floats above it (native's `ChatAttachmentMenu`), so
        // the CARD is its containing block. Without this the panel resolves against whatever
        // happens to be positioned further out and stops matching the composer's width.
        position: 'relative',
      };

  return (
    <div
      style={containerStyle}
      onFocus={collapsible ? () => setFocusWithin(true) : undefined}
      onBlur={
        collapsible
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusWithin(false);
            }
          : undefined
      }
    >
      {recordingNote ? (
        <RecordingBar
          variant="voiceNote"
          busy={voiceNote!.sending}
          seconds={voiceNote!.seconds}
          levels={voiceNote!.levels}
          cancelLabel={discardRecordingLabel ?? cancelLabel}
          sendLabel={sendVoiceNoteLabel ?? sendLabel}
          onCancel={voiceNote!.discard}
          onConfirm={voiceNote!.send}
          full={recordingLarge}
        />
      ) : recording ? (
        <RecordingBar
          seconds={dictation.seconds}
          levels={dictation.levels}
          cancelLabel={cancelLabel}
          sendLabel={sendLabel}
          onCancel={dictation.cancel}
          onConfirm={dictation.confirm}
          full={recordingLarge}
        />
      ) : voiceNoteError ? (
        <VoiceNoteNotice error={voiceNoteError} onDismiss={voiceNote!.dismissError} onRetry={voiceNote!.retry} />
      ) : (
        <>
          <div style={collapsed ? { flex: 1, minWidth: 0 } : { width: '100%' }}>
            {replyPreview}
            <textarea
            ref={textareaRef}
            className="orion-input"
            // Bidi: follow the direction of what's being TYPED (first strong char), falling back to the
            // UI language while empty so the placeholder sits naturally (mirrors native #6397/composer-direction.ts —
            // `dir="auto"` resolves an empty field to plain ltr, scrambling a mixed-language placeholder).
            dir={composerDir(value)}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              onInputActiveChange?.(e.target.value.length > 0);
            }}
            onFocus={() => {
              setFocused(true);
              onInputActiveChange?.(true);
            }}
            onBlur={() => {
              setFocused(false);
              onInputActiveChange?.(value.length > 0);
            }}
            onPaste={onPaste}
            onKeyDown={(e) => {
              // The Enter that confirms an IME (kana/kanji, pinyin, Hangul) candidate is not "send".
              if (isImeComposing(e)) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder={effectivePlaceholder}
            rows={1}
            disabled={transcribing}
            suppressHydrationWarning
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              borderRadius: 0,
              paddingBlock: 2,
              paddingInlineEnd: 2,
              paddingInlineStart: 6,
              resize: 'none',
              // Figma input field: 40px single-line box that grows with content up
              // to 4 rows (4 × 24px line-height + 4px padding = 100px) then scrolls.
              // `field-sizing: content` drives the auto-grow between min and max.
              fieldSizing: 'content',
              // Collapsed, the field is ONE LINE: 24 of line-height over its own 2+2 padding. The 40
              // floor is the expanded prompt box's single-line box (Figma), and holding it while
              // collapsed is what kept the resting bar reading as taller than one line.
              minHeight: collapsed ? 28 : 40,
              maxHeight: 100,
              overflowY: 'auto',
              // 16px matches the message body (and clears iOS's <16px zoom-on-focus).
              fontSize: 16,
              fontWeight: 400,
              // Figma prompt-box placeholder (14701:126614): 16px / 24 line-height / -0.32px tracking
              // (−2% of 16px). Applies to the placeholder and the typed text alike (one field).
              letterSpacing: '-0.32px',
              lineHeight: '24px',
              // NEVER enable discretionary ligatures on a text field: `dlig` ligates ま+す into the
              // masu mark 〼 (U+303C, the historical ligature of ます) in a CJK fallback font, so a
              // Japanese user's typed ます renders as 〼. A composer must render typed characters
              // literally in every script — disable ligatures outright (font-feature-settings
              // inherits, so this also blocks any ancestor turning `dlig` back on).
              fontVariantLigatures: 'none',
              // Typed text = primary ink; the placeholder is secondary (CSS below).
              color: 'var(--sym-ink)',
            }}
          />
          </div>
          {collapsed && sendControl}
          <div style={{ display: collapsed ? 'none' : 'flex', alignItems: 'center', width: '100%', gap: 8, marginTop: 12 }}>
            {onAttachContact ? (
              <AttachMenu
                triggerLabel={attachLabel}
                title={attachTitle ?? attachLabel}
                triggerClassName={styles.chatRound}
                items={[
                  // Take Photo leads, as it does on both phones (their Photo Library row above it has
                  // no honest web counterpart — see the note in Composer.tsx).
                  ...(onTakePhoto
                    ? [{ key: 'photo', label: takePhotoLabel ?? attachLabel, icon: <CameraMenuIcon />, onSelect: onTakePhoto } as AttachMenuItem]
                    : []),
                  ...(onPhonePhoto
                    ? [{ key: 'phonePhoto', label: phonePhotoLabel ?? attachLabel, icon: <PhoneMenuIcon />, onSelect: onPhonePhoto } as AttachMenuItem]
                    : []),
                  { key: 'file', label: chooseFileLabel ?? attachLabel, icon: <FileMenuIcon />, onSelect: onAttach },
                  ...(onAttachContact
                    ? [{ key: 'contact', label: contactLabel ?? attachLabel, icon: <ContactMenuIcon />, onSelect: onAttachContact } as AttachMenuItem]
                    : []),
                  ...(onAttachLocation
                    ? [{ key: 'location', label: locationLabel ?? attachLabel, icon: <LocationMenuIcon />, onSelect: onAttachLocation } as AttachMenuItem]
                    : []),
                  ...(onAttachTime
                    ? [{ key: 'time', label: shareTimeLabel ?? attachLabel, icon: <TimeMenuIcon />, onSelect: onAttachTime } as AttachMenuItem]
                    : []),
                ]}
              />
            ) : (
              <button type="button" onClick={onAttach} aria-label={attachLabel} title={attachLabel} style={{ ...ICON_BTN, borderRadius: 'var(--ds-radius-full)' }}>
                <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </button>
            )}
            <div style={{ flex: 1 }} />
            {micAvailable && !transcribing && (
              <IconButton size="small" onClick={onMicTap} aria-label={micLabel} title={micLabel} className={styles.chatRound}>
                {/* The resting glyph is always the mic on the phones too — only the BEHAVIOR is
                    modal (empty field records, typed text dictates). */}
                <Mic size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
            )}
            {transcribing && (
              <span aria-hidden className="orion-mic-live" style={{ ...ICON_BTN, color: 'var(--accent-solid)' }}>
                <TranscribingSpinner />
              </span>
            )}
            {showStop ? (
              <IconButton
                variant="contained"
                color="standard"
                size="small"
                onClick={onStop}
                aria-label={stopLabel ?? ''}
                className={styles.chatRound}
              >
                <Square size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
              </IconButton>
            ) : canSend || (focused && !speakWhenEmpty) || !onStartCall || !speakLabel ? (
              // Send shows once there's text OR the field is focused ("Keyboard Open" — disabled until
              // something is typed). Black round Send, per Figma 12835:79044 / "Typing". `speakWhenEmpty`
              // (the mobile FAB) keeps Speak while the empty field is focused; typing still swaps to Send.
              sendControl
            ) : (
              // Empty AND blurred ("Speak Default") → the black "Speak" pill that opens the live call
              // surface. Focusing or typing swaps Send back in (the swap fades/scales in).
              <Button
                size="small"
                onClick={onStartCall}
                title={startCallLabel}
                className={`${styles.swapIn} ${styles.chatRound}`}
                startIcon={<AudioLines size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
              >
                {speakLabel}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
